/**
 * Market Data Service
 *
 * Unified service for fetching live market data (stock prices, option prices,
 * forex rates) from the new /v2/market-data/* API surface.
 *
 * Replaces price-fetching methods previously scattered across:
 * - stockSearchService (getPrice, getPriceByIsinAndExchange, getBatchPrices)
 * - contractsService (getBatchPrices, getBatchPriceMap, getBatchPricesChunked)
 * - optionsService (getOptionPrices, getBatchOptionPrices)
 * - forexService (all methods)
 *
 * All endpoints are POST with JSON bodies, accept arrays, and return maps
 * keyed by identifier (conId or currency code).
 */

import {
    ChunkedProgressCallback,
    ForexRateResult,
    ForexRatesResponse,
    OptionPriceResult,
    OptionPricesResponse,
    ShortableDataResponse,
    ShortableDataResult,
    StockPriceAllSourceEntry,
    StockPriceHistoryResponse,
    StockPriceResult,
    StockPricesAllResponse,
    StockPricesResponse,
} from "../types/marketData";
import httpService from "./httpService";

// ============================================================================
// Constants
// ============================================================================

/** Default chunk size for batched stock price requests */
const DEFAULT_CHUNK_SIZE = 25;

/** Default max concurrent chunk requests */
const DEFAULT_MAX_CONCURRENT = 3;

/** Default retry attempts per chunk */
const DEFAULT_RETRY_ATTEMPTS = 2;

/** Base retry delay in milliseconds (exponential backoff) */
const RETRY_DELAY_BASE_MS = 1000;

// ============================================================================
// Stock Prices
// ============================================================================

/**
 * Direct single-request fetch. Internal only -- called by fetchChunkWithRetry.
 * Public callers use getStockPrices or getStockPrice, both of which route
 * through getStockPricesChunked for batching.
 */
async function fetchStockPricesDirect(
  conIds: number[]
): Promise<Record<number, StockPriceResult>> {
  if (conIds.length === 0) {
    return {};
  }

  const response = await httpService.post<StockPricesResponse>(
    "/v2/market-data/stocks",
    { conIds }
  );

  // API returns string keys -- normalize to numeric keys
  return normalizeNumericKeys(response.data);
}

/**
 * Fetch live stock prices for multiple contracts.
 *
 * Automatically batches large requests into chunks with concurrency control
 * and per-chunk retry logic. Small requests (<= chunk size) execute as a
 * single request with zero overhead.
 *
 * Throws when no prices are returned for any requested conId (total failure).
 * Returns partial results silently when some chunks succeed and others fail.
 *
 * @param conIds - Array of contract IDs to fetch prices for
 * @returns Map of conId to StockPriceResult (aggregated from all chunks)
 * @throws When all chunks fail after retries (empty result for non-empty input)
 */
async function getStockPrices(
  conIds: number[]
): Promise<Record<number, StockPriceResult>> {
  const result = await getStockPricesChunked(conIds);

  // Total failure: conIds were requested but nothing came back.
  // Throw to preserve legacy error semantics for provider callers' try/catch blocks.
  if (conIds.length > 0 && Object.keys(result).length === 0) {
    throw new Error(
      `marketDataService: failed to fetch prices for ${conIds.length} conIds`
    );
  }

  return result;
}

/**
 * Fetch live stock price for a single contract.
 * Bypasses getStockPrices to avoid its empty-result throw logic.
 * Returns null when price is not found or when fetch fails after retries.
 *
 * @param conId - Contract ID to fetch price for
 * @returns StockPriceResult or null if not found / fetch failed
 */
async function getStockPrice(
  conId: number
): Promise<StockPriceResult | null> {
  const result = await getStockPricesChunked([conId]);
  return result[conId] ?? null;
}

/**
 * Fetch stock prices with chunking and progressive loading.
 *
 * Splits large requests into smaller chunks, executes them in parallel with
 * controlled concurrency, and invokes onProgress as each chunk completes.
 *
 * @param conIds - Array of contract IDs to fetch prices for
 * @param options - Chunking configuration
 * @returns Map of conId to StockPriceResult
 */
async function getStockPricesChunked(
  conIds: number[],
  options?: {
    chunkSize?: number;
    maxConcurrent?: number;
    retryAttempts?: number;
    onProgress?: ChunkedProgressCallback;
  }
): Promise<Record<number, StockPriceResult>> {
  if (conIds.length === 0) {
    return {};
  }

  const chunkSize = options?.chunkSize ?? DEFAULT_CHUNK_SIZE;
  const maxConcurrent = options?.maxConcurrent ?? DEFAULT_MAX_CONCURRENT;
  const retryAttempts = options?.retryAttempts ?? DEFAULT_RETRY_ATTEMPTS;
  const onProgress = options?.onProgress;

  // Split conIds into chunks
  const chunks: number[][] = [];
  for (let i = 0; i < conIds.length; i += chunkSize) {
    chunks.push(conIds.slice(i, i + chunkSize));
  }

  // Single chunk -- no chunking overhead needed
  if (chunks.length === 1) {
    const result = await fetchChunkWithRetry(chunks[0], 0, retryAttempts);
    if (onProgress) {
      onProgress(result, 1, 1);
    }
    return result;
  }

  // Track cumulative results for progressive updates
  const cumulativeResults: Record<number, StockPriceResult> = {};
  let completedChunks = 0;

  // Create tasks for each chunk
  const tasks = chunks.map(
    (chunk, index) => async () => {
      const chunkResult = await fetchChunkWithRetry(chunk, index, retryAttempts);
      return chunkResult;
    }
  );

  // Execute with concurrency limit and progress callbacks
  await executeWithConcurrencyLimit(
    tasks,
    maxConcurrent,
    (chunkResult) => {
      Object.assign(cumulativeResults, chunkResult);
      completedChunks++;

      if (onProgress) {
        onProgress({ ...cumulativeResults }, completedChunks, chunks.length);
      }
    }
  );

  return cumulativeResults;
}

// ============================================================================
// Stock Prices -- All Sources (Admin Comparer)
// ============================================================================

/**
 * Fetch stock prices from all sources (IBKR, EODHD, YAHOO) for comparison.
 * Admin-only endpoint -- fetches in parallel from all sources with no caching.
 *
 * @param conIds - Array of contract IDs to fetch prices for
 * @returns Map of conId to array of per-source price entries
 */
async function getStockPricesAll(
  conIds: number[]
): Promise<Record<number, StockPriceAllSourceEntry[]>> {
  if (conIds.length === 0) {
    return {};
  }

  const response = await httpService.post<StockPricesAllResponse>(
    "/v2/market-data/stocks/all",
    { conIds }
  );

  return normalizeNumericKeys(response.data);
}

// ============================================================================
// Stock Price History (Admin -- per-source historical bars)
// ============================================================================

/**
 * Fetch historical price bars for a single contract from a specific source.
 *
 * @param conId - Contract ID
 * @param source - Price source ("IBKR" or "EODHD")
 * @param duration - Lookback duration (e.g. "7D")
 * @param interval - Bar interval (e.g. "30m")
 */
async function getStockPriceHistory(
  conId: number,
  source: "IBKR" | "EODHD" | "BLOOMBERG",
  duration: string = "10D",
  interval: string = "30m"
): Promise<StockPriceHistoryResponse> {
  const response = await httpService.post<StockPriceHistoryResponse>(
    `/v2/market-data/stocks/history?source=${source}`,
    { conId, duration, interval }
  );
  return response.data;
}

// ============================================================================
// Stock Shortable Status (Admin -- IBKR borrow availability)
// ============================================================================

/**
 * Fetch shortable availability (borrow status and shares available to borrow)
 * for stocks. Admin-only endpoint -- fetches live from IBKR with no caching.
 *
 * @param conIds - Array of contract IDs to fetch shortable status for
 * @returns Map of conId to ShortableDataResult
 */
async function getStockShortable(
  conIds: number[]
): Promise<Record<number, ShortableDataResult>> {
  if (conIds.length === 0) {
    return {};
  }

  const response = await httpService.post<ShortableDataResponse>(
    "/v2/market-data/stocks/shortable",
    { conIds }
  );

  const results: Record<number, ShortableDataResult> = {};
  for (const result of response.data.results) {
    results[result.conId] = result;
  }
  return results;
}

// ============================================================================
// Option Prices
// ============================================================================

/**
 * Fetch live option prices for multiple contracts in a single request.
 *
 * @param conIds - Array of option contract IDs to fetch prices for
 * @returns Map of conId to OptionPriceResult
 */
async function getOptionPrices(
  conIds: number[]
): Promise<Record<number, OptionPriceResult>> {
  if (conIds.length === 0) {
    return {};
  }

  const response = await httpService.post<OptionPricesResponse>(
    "/v2/market-data/options",
    { conIds }
  );

  return normalizeNumericKeys(response.data);
}

// ============================================================================
// Forex Rates
// ============================================================================

/**
 * Fetch live forex rates to USD for multiple currencies in a single request.
 *
 * @param currencies - Array of currency codes (e.g. ["EUR", "GBP"])
 * @returns Map of currency code to ForexRateResult
 */
async function getForexRates(
  currencies: string[]
): Promise<Record<string, ForexRateResult>> {
  if (currencies.length === 0) {
    return {};
  }

  const response = await httpService.post<ForexRatesResponse>(
    "/v2/market-data/forex",
    { currencies }
  );

  return response.data;
}

/**
 * Fetch live forex rate to USD for a single currency.
 * Convenience wrapper that short-circuits for USD.
 *
 * @param currency - Currency code (e.g. "GBP")
 * @returns Exchange rate to USD (1 unit of currency = rate USD)
 */
async function getForexRateToUsd(currency: string): Promise<number> {
  if (currency.toUpperCase() === "USD") {
    return 1.0;
  }

  const result = await getForexRates([currency]);
  const entry = result[currency.toUpperCase()];
  if (!entry) {
    throw new Error(`Forex rate not found for currency: ${currency}`);
  }
  return entry.rate;
}

// ============================================================================
// Internal Helpers
// ============================================================================

/**
 * Normalize string-keyed response maps to numeric keys.
 * The API returns JSON objects with string keys (e.g. "265598"), but our
 * consumers expect numeric conId keys.
 */
function normalizeNumericKeys<T>(
  data: Record<string, T>
): Record<number, T> {
  const result: Record<number, T> = {};
  for (const [key, value] of Object.entries(data)) {
    const numKey = Number(key);
    if (Number.isFinite(numKey)) {
      result[numKey] = value;
    }
  }
  return result;
}

/**
 * Fetch a single chunk of stock prices with retry logic.
 */
async function fetchChunkWithRetry(
  chunk: number[],
  chunkIndex: number,
  retryAttempts: number
): Promise<Record<number, StockPriceResult>> {
  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= retryAttempts; attempt++) {
    try {
      if (attempt > 0) {
        // Exponential backoff: 1s, 2s, 4s...
        const delay = RETRY_DELAY_BASE_MS * Math.pow(2, attempt - 1);
        await new Promise(resolve => setTimeout(resolve, delay));
      }

      return await fetchStockPricesDirect(chunk);
    } catch (error) {
      lastError = error instanceof Error ? error : new Error(String(error));
      console.warn(
        `marketDataService: chunk ${chunkIndex} attempt ${attempt + 1} failed:`,
        lastError.message
      );
    }
  }

  // All retries exhausted -- return empty for this chunk rather than failing entirely
  console.error(
    `marketDataService: chunk ${chunkIndex} failed after ${retryAttempts + 1} attempts`
  );
  return {};
}

/**
 * Execute async tasks with controlled concurrency using a semaphore pattern.
 */
async function executeWithConcurrencyLimit<T>(
  tasks: Array<() => Promise<T>>,
  limit: number,
  onTaskComplete?: (result: T, index: number) => void
): Promise<void> {
  let nextIndex = 0;

  const executeNext = async (): Promise<void> => {
    while (nextIndex < tasks.length) {
      const currentIndex = nextIndex++;
      const task = tasks[currentIndex];

      try {
        const value = await task();
        if (onTaskComplete) {
          onTaskComplete(value, currentIndex);
        }
      } catch {
        // Individual chunk failures are handled inside fetchChunkWithRetry
      }
    }
  };

  // Start up to 'limit' concurrent workers
  const workers = Array(Math.min(limit, tasks.length))
    .fill(null)
    .map(() => executeNext());

  await Promise.all(workers);
}

// ============================================================================
// Service Export
// ============================================================================

const marketDataService = {
  getStockPrices,
  getStockPrice,
  getStockPricesChunked,
  getStockPricesAll,
  getStockPriceHistory,
  getStockShortable,
  getOptionPrices,
  getForexRates,
  getForexRateToUsd,
};

export default marketDataService;
export {
    getForexRateToUsd, getForexRates, getOptionPrices, getStockPrice, getStockPriceHistory, getStockPrices, getStockPricesAll, getStockPricesChunked, getStockShortable
};
